/**
 * CSS 变量生成器
 * 将设计令牌应用到 DOM 的 CSS 变量
 */

import type { DesignTokens } from './types';

/**
 * 将设计令牌应用为 CSS 变量
 * @param tokens 设计令牌
 * @param root 目标元素，默认为 document.documentElement
 */
export function applyTokensToCSS(tokens: DesignTokens, root: HTMLElement = document.documentElement): void {
  const { colors, typography, spacing, borderRadius, shadow } = tokens;

  // ========== 颜色 ==========
  // 品牌色
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-primary-hover', colors.primaryHover);
  root.style.setProperty('--color-primary-active', colors.primaryActive);
  root.style.setProperty('--color-primary-disabled', colors.primaryDisabled);

  // 功能色
  root.style.setProperty('--color-success', colors.success);
  root.style.setProperty('--color-success-hover', colors.successHover);
  root.style.setProperty('--color-warning', colors.warning);
  root.style.setProperty('--color-warning-hover', colors.warningHover);
  root.style.setProperty('--color-error', colors.error);
  root.style.setProperty('--color-error-hover', colors.errorHover);
  root.style.setProperty('--color-info', colors.info);
  root.style.setProperty('--color-info-hover', colors.infoHover);

  // 文本颜色
  root.style.setProperty('--color-text-primary', colors.text.primary);
  root.style.setProperty('--color-text-secondary', colors.text.secondary);
  root.style.setProperty('--color-text-tertiary', colors.text.tertiary);
  root.style.setProperty('--color-text-disabled', colors.text.disabled);
  root.style.setProperty('--color-text-inverse', colors.text.inverse);
  root.style.setProperty('--color-text-placeholder', colors.text.placeholder);

  // 边框颜色
  root.style.setProperty('--color-border-base', colors.border.base);
  root.style.setProperty('--color-border-light', colors.border.light);
  root.style.setProperty('--color-border-dark', colors.border.dark);
  root.style.setProperty('--color-border-focus', colors.border.focus);

  // 背景颜色
  root.style.setProperty('--color-bg-base', colors.background.base);
  root.style.setProperty('--color-bg-elevated', colors.background.elevated);
  root.style.setProperty('--color-bg-hover', colors.background.hover);
  root.style.setProperty('--color-bg-active', colors.background.active);
  root.style.setProperty('--color-bg-overlay', colors.background.overlay);
  root.style.setProperty('--color-bg-disabled', colors.background.disabled);

  // ========== 字体 ==========
  // 字体族
  root.style.setProperty('--font-family-base', typography.fontFamily.base);
  root.style.setProperty('--font-family-heading', typography.fontFamily.heading);
  root.style.setProperty('--font-family-code', typography.fontFamily.code);

  // 字体大小
  root.style.setProperty('--font-size-xs', typography.fontSize.xs);
  root.style.setProperty('--font-size-sm', typography.fontSize.sm);
  root.style.setProperty('--font-size-base', typography.fontSize.base);
  root.style.setProperty('--font-size-lg', typography.fontSize.lg);
  root.style.setProperty('--font-size-xl', typography.fontSize.xl);
  root.style.setProperty('--font-size-2xl', typography.fontSize['2xl']);
  root.style.setProperty('--font-size-3xl', typography.fontSize['3xl']);
  root.style.setProperty('--font-size-4xl', typography.fontSize['4xl']);

  // 字重
  root.style.setProperty('--font-weight-normal', String(typography.fontWeight.normal));
  root.style.setProperty('--font-weight-medium', String(typography.fontWeight.medium));
  root.style.setProperty('--font-weight-semibold', String(typography.fontWeight.semibold));
  root.style.setProperty('--font-weight-bold', String(typography.fontWeight.bold));

  // 行高
  root.style.setProperty('--line-height-tight', String(typography.lineHeight.tight));
  root.style.setProperty('--line-height-base', String(typography.lineHeight.base));
  root.style.setProperty('--line-height-relaxed', String(typography.lineHeight.relaxed));
  root.style.setProperty('--line-height-loose', String(typography.lineHeight.loose));

  // ========== 间距 ==========
  root.style.setProperty('--spacing-xs', spacing.xs);
  root.style.setProperty('--spacing-sm', spacing.sm);
  root.style.setProperty('--spacing-md', spacing.md);
  root.style.setProperty('--spacing-lg', spacing.lg);
  root.style.setProperty('--spacing-xl', spacing.xl);
  root.style.setProperty('--spacing-2xl', spacing['2xl']);
  root.style.setProperty('--spacing-3xl', spacing['3xl']);

  // ========== 圆角 ==========
  root.style.setProperty('--radius-none', borderRadius.none);
  root.style.setProperty('--radius-sm', borderRadius.sm);
  root.style.setProperty('--radius-base', borderRadius.base);
  root.style.setProperty('--radius-md', borderRadius.md);
  root.style.setProperty('--radius-lg', borderRadius.lg);
  root.style.setProperty('--radius-xl', borderRadius.xl);
  root.style.setProperty('--radius-2xl', borderRadius['2xl']);
  root.style.setProperty('--radius-full', borderRadius.full);

  // ========== 阴影 ==========
  root.style.setProperty('--shadow-none', shadow.none);
  root.style.setProperty('--shadow-sm', shadow.sm);
  root.style.setProperty('--shadow-base', shadow.base);
  root.style.setProperty('--shadow-md', shadow.md);
  root.style.setProperty('--shadow-lg', shadow.lg);
  root.style.setProperty('--shadow-xl', shadow.xl);
}

/**
 * 移除所有主题相关的 CSS 变量
 * @param root 目标元素，默认为 document.documentElement
 */
export function removeThemeCSS(root: HTMLElement = document.documentElement): void {
  const properties = [
    // 颜色
    '--color-primary', '--color-primary-hover', '--color-primary-active', '--color-primary-disabled',
    '--color-success', '--color-success-hover', '--color-warning', '--color-warning-hover',
    '--color-error', '--color-error-hover', '--color-info', '--color-info-hover',
    '--color-text-primary', '--color-text-secondary', '--color-text-tertiary',
    '--color-text-disabled', '--color-text-inverse', '--color-text-placeholder',
    '--color-border-base', '--color-border-light', '--color-border-dark', '--color-border-focus',
    '--color-bg-base', '--color-bg-elevated', '--color-bg-hover', '--color-bg-active',
    '--color-bg-overlay', '--color-bg-disabled',
    // 字体
    '--font-family-base', '--font-family-heading', '--font-family-code',
    '--font-size-xs', '--font-size-sm', '--font-size-base', '--font-size-lg',
    '--font-size-xl', '--font-size-2xl', '--font-size-3xl', '--font-size-4xl',
    '--font-weight-normal', '--font-weight-medium', '--font-weight-semibold', '--font-weight-bold',
    '--line-height-tight', '--line-height-base', '--line-height-relaxed', '--line-height-loose',
    // 间距
    '--spacing-xs', '--spacing-sm', '--spacing-md', '--spacing-lg',
    '--spacing-xl', '--spacing-2xl', '--spacing-3xl',
    // 圆角
    '--radius-none', '--radius-sm', '--radius-base', '--radius-md',
    '--radius-lg', '--radius-xl', '--radius-2xl', '--radius-full',
    // 阴影
    '--shadow-none', '--shadow-sm', '--shadow-base', '--shadow-md',
    '--shadow-lg', '--shadow-xl',
  ];

  properties.forEach(property => {
    root.style.removeProperty(property);
  });
}

/**
 * 获取当前 CSS 变量值
 * @param variableName CSS 变量名（不含 -- 前缀）
 * @param root 目标元素，默认为 document.documentElement
 */
export function getCSSVariable(variableName: string, root: HTMLElement = document.documentElement): string {
  return getComputedStyle(root).getPropertyValue(`--${variableName}`).trim();
}
